import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Mic, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

const AuthPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('signin');

  const { signIn, signUp, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in.');
        } else {
          setError(error.message);
        }
      } else {
        toast({
          title: "Welcome back!",
          description: "You have been successfully signed in.",
        });
        navigate('/');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const { error } = await signUp(email, password);

      if (error) {
        if (error.message.includes('User already registered')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else if (error.message.includes('Password should be')) {
          setError('Password is too weak. Please use a stronger password.');
        } else {
          setError(error.message);
        }
      } else {
        toast({
          title: "Account Created!",
          description: "Please check your email to confirm your account before signing in.",
        });
        setActiveTab('signin');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Sign up error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist flex items-center justify-center p-6 relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-full bg-console-primary/15 border border-console-primary/30 flex items-center justify-center">
            <Shield className="h-6 w-6 text-console-primary" />
          </div>
          <div className="space-y-1.5">
            <span className="font-geist-mono text-[11px] tracking-[0.2em] uppercase text-console-primary">00 / Admin Portal</span>
            <h1 className="font-geist text-[36px] font-semibold tracking-[-0.03em] leading-none text-console-text">KaraokeCore</h1>
            <p className="font-geist text-[13px] text-console-muted">Authentication</p>
          </div>
        </div>

        {/* Back to Home */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="w-full h-11 sm:h-9 flex items-center gap-2 font-geist text-[13px] text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>

        {/* Auth Form */}
        <Card className="bg-console-surface border-console-line rounded-[12px]">
          <CardHeader className="space-y-1">
            <CardTitle className="font-geist text-[20px] font-semibold tracking-tight text-center text-console-text">Sign in</CardTitle>
            <CardDescription className="font-geist text-[13px] text-center text-console-muted">
              Sign in to access SuperAdmin features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 bg-console-bg border border-console-line rounded-[6px] p-1">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <TabsContent value="signin" className="space-y-4">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="signin-email" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="admin@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0 font-geist rounded-[6px]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0 font-geist rounded-[6px]"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 px-5 font-geist font-semibold text-[14px] bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                    disabled={loading}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="space-y-4">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="nairoke@karaokecore.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0 font-geist rounded-[6px]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0 font-geist rounded-[6px]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0 font-geist rounded-[6px]"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 px-5 font-geist font-semibold text-[14px] bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create SuperAdmin Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <div className="flex items-center gap-2 justify-center">
                <Mic className="h-4 w-4" />
                <span>Secure admin access for KaraokeCore</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;